import { useMemo, useState, type Dispatch, type MouseEvent, type SetStateAction } from 'react';
import { Box, CircularProgress, Menu, MenuItem } from '@mui/material';
import { CheckRounded as CheckIcon, CloseRounded as CloseIcon } from '@mui/icons-material';
import { headBg, typeLabel } from './styles';
import { useDeviceFilterOptions } from '@/hooks/use-devices';
import { LAST_SEEN_OPTIONS, toDeviceFilter } from '@/lib/device-filter-options';
import type { ShellJobDeviceFilter } from '@/lib/shell-job.service';

type ListKey = 'model' | 'agent_version' | 'os_version' | 'rom_version';
type FacetKey = 'type' | 'status' | ListKey | 'is_internal';

const STATUS: Record<string, string> = { online: 'Online', offline: 'Offline', pending: 'Pending', blocked: 'Blocked' };
const OWNERSHIP: Record<string, string> = { false: 'Customer', true: 'Internal' };
const SOURCE: Record<ListKey, 'models' | 'agent_versions' | 'os_versions' | 'rom_versions'> = {
  model: 'models',
  agent_version: 'agent_versions',
  os_version: 'os_versions',
  rom_version: 'rom_versions',
};

/** The mockup's facets, in its order. Ownership is an Aviron-side concept: super admins only. */
const FACETS: { key: FacetKey; label: string; multi: boolean }[] = [
  { key: 'type', label: 'Type', multi: true },
  { key: 'status', label: 'Status', multi: false },
  { key: 'model', label: 'Model', multi: true },
  { key: 'agent_version', label: 'Agent version', multi: true },
  { key: 'os_version', label: 'OS version', multi: true },
  { key: 'rom_version', label: 'ROM', multi: true },
  { key: 'is_internal', label: 'Ownership', multi: false },
];

const chipSx = {
  display: 'inline-flex',
  alignItems: 'center',
  maxWidth: '100%',
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: '6px',
  font: 'inherit',
  fontSize: 13,
  lineHeight: 1.5,
} as const;
const bareButton = { border: 0, p: 0, bgcolor: 'transparent', color: 'inherit', font: 'inherit', cursor: 'pointer' } as const;

function SetChip({ text, clearLabel, onOpen, onClear }: { text: string; clearLabel: string; onOpen?: (e: MouseEvent<HTMLElement>) => void; onClear: () => void }) {
  const textSx = { pl: '10px', py: '4px', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } as const;
  return (
    <Box sx={{ ...chipSx, color: 'text.primary', bgcolor: headBg }}>
      {onOpen ? (
        <Box component="button" type="button" onClick={onOpen} sx={{ ...bareButton, ...textSx }}>
          {text}
        </Box>
      ) : (
        <Box component="span" sx={textSx}>
          {text}
        </Box>
      )}
      <Box component="button" type="button" aria-label={clearLabel} onClick={onClear} sx={{ ...bareButton, display: 'inline-flex', px: '6px', py: '4px' }}>
        <CloseIcon sx={{ fontSize: 15 }} />
      </Box>
    </Box>
  );
}

interface FacetChipsProps {
  value: ShellJobDeviceFilter;
  /** A state setter: a click can apply several changes in a row, each on top of the last. */
  onChange: Dispatch<SetStateAction<ShellJobDeviceFilter>>;
  tenantId?: string;
  showOwnership: boolean;
}

/**
 * The New run drawer's Filter mode: a chip per set facet ("Type: Rower ✕") and
 * a dashed "+ Model" for each facet still open. A chip opens its values; ✕
 * clears it. Facets the drawer does not offer but a "Run again" filter carries
 * (activity, search, sub-type) show as chips too, so nothing filters unseen.
 */
export function FacetChips({ value, onChange, tenantId, showOwnership }: FacetChipsProps) {
  const scoped = useMemo(() => toDeviceFilter(value, tenantId), [value, tenantId]);
  const { data: options, isLoading: optionsLoading } = useDeviceFilterOptions(scoped);
  // Menus open at a fixed point: the "+ Model" that opened one is replaced by
  // a "Model: …" chip as soon as a value is picked.
  const [menu, setMenu] = useState<{ key: FacetKey; top: number; left: number } | null>(null);

  // Several raw type codes can read the same ("0" and "rower" are both Rower).
  const typeCodes = useMemo(() => {
    const m = new Map<string, string[]>();
    for (const raw of [...(options?.types ?? []), ...(value.type ?? [])]) {
      const label = typeLabel(raw);
      m.set(label, [...new Set([...(m.get(label) ?? []), raw])]);
    }
    return m;
  }, [options?.types, value.type]);

  const choices = (key: FacetKey): { value: string; label: string }[] => {
    if (key === 'type') return [...typeCodes.keys()].map((l) => ({ value: l, label: l }));
    if (key === 'status') return Object.entries(STATUS).map(([v, l]) => ({ value: v, label: l }));
    if (key === 'is_internal') return Object.entries(OWNERSHIP).map(([v, l]) => ({ value: v, label: l }));
    return (options?.[SOURCE[key]] ?? []).map((v) => ({ value: v, label: v }));
  };

  const picked = (key: FacetKey): string[] => {
    if (key === 'type') return [...typeCodes].filter(([, codes]) => codes.some((c) => value.type?.includes(c))).map(([l]) => l);
    if (key === 'status') return value.status ? [value.status] : [];
    if (key === 'is_internal') return value.is_internal === undefined ? [] : [String(value.is_internal)];
    return value[key] ?? [];
  };

  const toggle = (key: FacetKey, v: string) => {
    if (key === 'status') return onChange((f) => ({ ...f, status: f.status === v ? undefined : v }));
    if (key === 'is_internal') return onChange((f) => ({ ...f, is_internal: String(f.is_internal) === v ? undefined : v === 'true' }));
    if (key === 'type') {
      const codes = typeCodes.get(v) ?? [v];
      return onChange((f) => {
        const cur = f.type ?? [];
        const on = codes.some((c) => cur.includes(c));
        return { ...f, type: on ? cur.filter((c) => !codes.includes(c)) : [...new Set([...cur, ...codes])] };
      });
    }
    onChange((f) => {
      const cur = f[key] ?? [];
      return { ...f, [key]: cur.includes(v) ? cur.filter((x) => x !== v) : [...cur, v] };
    });
  };

  const clear = (key: keyof ShellJobDeviceFilter) => onChange((f) => ({ ...f, [key]: undefined }));
  const openMenu = (key: FacetKey) => (e: MouseEvent<HTMLElement>) => {
    const r = e.currentTarget.getBoundingClientRect();
    setMenu({ key, top: r.bottom + 4, left: r.left });
  };
  const labelOf = (key: FacetKey, v: string) => choices(key).find((c) => c.value === v)?.label ?? v;

  const facets = FACETS.filter((f) => f.key !== 'is_internal' || showOwnership || value.is_internal !== undefined);
  const set = facets.filter((f) => picked(f.key).length > 0);
  const unset = facets.filter((f) => picked(f.key).length === 0);
  const extras: { key: 'last_seen' | 'sub_type' | 'search'; text: string }[] = [];
  if (value.last_seen) extras.push({ key: 'last_seen', text: `Activity: ${LAST_SEEN_OPTIONS.find((o) => o.value === value.last_seen)?.label ?? value.last_seen}` });
  if (value.sub_type?.length) extras.push({ key: 'sub_type', text: `Sub-type: ${value.sub_type.join(', ')}` });
  if (value.search) extras.push({ key: 'search', text: `Search: ${value.search}` });
  const current = menu && facets.find((f) => f.key === menu.key);
  const currentChoices = current ? choices(current.key) : [];

  return (
    <Box sx={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
      {set.map((f) => (
        <SetChip
          key={f.key}
          text={`${f.label}: ${picked(f.key).map((v) => labelOf(f.key, v)).join(', ')}`}
          clearLabel={`Clear ${f.label}`}
          onOpen={openMenu(f.key)}
          onClear={() => clear(f.key)}
        />
      ))}
      {extras.map((x) => (
        <SetChip key={x.key} text={x.text} clearLabel={`Clear ${x.text.split(':')[0]}`} onClear={() => clear(x.key)} />
      ))}
      {unset.map((f) => (
        <Box
          key={f.key}
          component="button"
          type="button"
          onClick={openMenu(f.key)}
          sx={{ ...chipSx, borderStyle: 'dashed', bgcolor: 'transparent', color: 'text.secondary', px: '10px', py: '4px', cursor: 'pointer', '&:hover': { color: 'text.primary' } }}
        >
          + {f.label}
        </Box>
      ))}
      <Menu
        open={!!current}
        onClose={() => setMenu(null)}
        anchorReference="anchorPosition"
        anchorPosition={menu ? { top: menu.top, left: menu.left } : undefined}
        slotProps={{ paper: { sx: { maxHeight: 320, minWidth: 200 } } }}
      >
        {currentChoices.length === 0 && (
          <MenuItem disabled dense sx={{ fontSize: 13, gap: 1 }}>
            {/* The first options request is still out: say so, not "No values". */}
            {optionsLoading ? (
              <>
                <CircularProgress size={12} />
                Loading…
              </>
            ) : (
              'No values'
            )}
          </MenuItem>
        )}
        {current &&
          currentChoices.map((c) => {
            const on = picked(current.key).includes(c.value);
            return (
              <MenuItem
                key={c.value}
                dense
                selected={on}
                onClick={() => {
                  toggle(current.key, c.value);
                  if (!current.multi) setMenu(null);
                }}
                sx={{ fontSize: 13, gap: 1 }}
              >
                <CheckIcon sx={{ fontSize: 16, visibility: on ? 'visible' : 'hidden' }} />
                {c.label}
              </MenuItem>
            );
          })}
      </Menu>
    </Box>
  );
}
